import {Component, computed, inject, signal} from '@angular/core';
import {BaseModalComponent} from "../../../../../../core/components/modals/base-modal/base-modal.component";
import {
  DateRange,
  HandleFilterSelectParams,
  ProductCardRatingDialogData,
  ProductCardRatingWeekly
} from "./types/pc-rating-modal.types";
import {MAT_DIALOG_DATA} from "@angular/material/dialog";
import {RecordsDataRequestQueryParams} from "../../../../../../core/types/records-table.types";
import {productCardsWeeklyRatingsResourceType, ratingModalName} from './configs/pc-rating-modal-config';
import {
  defaultRecordsDataRequestQueryParams
} from "../../../../../../core/models/default-records-data-request-query-params";
import {PcRatingService} from "./services/pc-rating.service";
import {MatButton} from "@angular/material/button";
import {PcRatingFiltersComponent} from "./components/pc-rating-filters/pc-rating-filters.component";
import {PcRatingHeaderComponent} from "./components/pc-rating-header/pc-rating-header.component";
import {PcRatingBodyComponent} from "./components/pc-rating-body/pc-rating-body.component";

@Component({
  selector: 'app-pc-rating-modal',
  imports: [
    BaseModalComponent,
    MatButton,
    PcRatingFiltersComponent,
    PcRatingHeaderComponent,
    PcRatingBodyComponent
  ],
  templateUrl: './pc-rating-modal.component.html',
  styleUrl: './pc-rating-modal.component.scss'
})
export class PcRatingModalComponent extends BaseModalComponent {
  private productCardRatingDialogData = inject<ProductCardRatingDialogData>(MAT_DIALOG_DATA);
  private pcRatingService: PcRatingService = inject(PcRatingService);

  public readonly productCardsWeeklyRatingsResourceType = productCardsWeeklyRatingsResourceType;
  public ratingData = signal<Array<ProductCardRatingWeekly>>([]);
  public weeklyCount = signal<number>(0);
  private queryParams = signal<RecordsDataRequestQueryParams>(defaultRecordsDataRequestQueryParams);
  public productCardId = computed(() => this.productCardRatingDialogData.productCardId || 0);

  public override async ngOnInit(): Promise<void> {
    super.ngOnInit();
    this.queryParams.set(this.pcRatingService.setQueryParams(this.productCardId()));
    this.modalStatesService.openModal(ratingModalName);
    this.loadingService.setIsLoadingState(true);
    await this.getRatingData();
    this.weeklyCount.set(this.getWeeklyCount());
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modalStatesService.closeModal(ratingModalName);
  }

  public async handleFilterSelect({ key, value }: HandleFilterSelectParams): Promise<void> {
    this.queryParams.update(current => {
      const { [key]: omitted, ...rest } = current;
      return {
        ...rest,
        ...(value ? { [key]: value } : {})
      };
    });
    await this.getRatingData();
  }

  public async handleFilterDate(dateRange: DateRange): Promise<void> {
    this.queryParams.update(current => {
      const { dateFrom: _, dateTo: __, ...baseParams } = current;
      return {
        ...baseParams,
        ...(dateRange.dateFrom ? { dateFrom: dateRange.dateFrom } : {}),
        ...(dateRange.dateTo ? { dateTo: dateRange.dateTo } : {})
      };
    });
    await this.getRatingData();
  }

  private async getRatingData(): Promise<void> {
    await this.pcRatingService.getWeeklyRating(this.queryParams)
      .then((data) => {
        const filterData = data.filter(i => i.article);
        this.ratingData.set(this.pcRatingService.normalizeRatingData(filterData));
      })
      .finally(() => this.loadingService.setIsLoadingState(false));
  }

  private getWeeklyCount(): number {
    return Math.max(...this.ratingData().map(item => item.weeklyRatings.length), 0);
  }
}
